/**
 * États UX obligatoires (bible §20) — le système n'affiche JAMAIS une page
 * vide sans expliquer pourquoi. Chaque module les réutilise tels quels.
 */
import type { ReactNode } from "react";
import { Icon } from "../icons";

function StateFrame({ icon, title, children, tone = "neutral" }: {
  icon: string;
  title: string;
  children?: ReactNode;
  tone?: "neutral" | "warn" | "danger";
}) {
  const bg =
    tone === "danger" ? "var(--danger-100)" : tone === "warn" ? "var(--warning-100)" : "var(--surface-subtle)";
  const fg =
    tone === "danger" ? "var(--danger-600)" : tone === "warn" ? "var(--warning-600)" : "var(--text-muted)";
  return (
    <div style={{ display: "grid", placeItems: "center", padding: 40, minHeight: 240 }}>
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            display: "grid",
            placeItems: "center",
            margin: "0 auto 16px",
            background: bg,
            color: fg,
          }}
        >
          <Icon id={icon} size="lg" />
        </div>
        <h4
          style={{
            margin: "0 0 8px",
            fontSize: 18,
            lineHeight: "26px",
            fontWeight: 700,
            // Décision Dove 01/09 : une erreur se voit en ROUGE, pas seulement
            // par son icône — « Connexion perdue » doit sauter aux yeux.
            color: tone === "danger" ? "var(--danger-600)" : tone === "warn" ? "var(--warning-600)" : undefined,
          }}
        >
          {title}
        </h4>
        <div style={{ color: "var(--text-secondary)", fontSize: 14.5, lineHeight: "22px" }}>{children}</div>
      </div>
    </div>
  );
}

export function LoadingState({ label = "Chargement…" }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" style={{ display: "grid", placeItems: "center", padding: 40, minHeight: 240, color: "var(--text-muted)" }}>
      {label}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string | undefined; action?: ReactNode }) {
  return (
    <StateFrame icon="i-info" title={title}>
      {hint}
      {action ? <div style={{ marginTop: 14 }}>{action}</div> : null}
    </StateFrame>
  );
}

export function ErrorState({ title = "Une erreur est survenue", detail, retry }: {
  title?: string;
  detail?: string | undefined;
  retry?: (() => void) | undefined;
}) {
  return (
    <StateFrame icon="i-alert" title={title} tone="danger">
      {detail}
      {retry ? (
        <div style={{ marginTop: 14 }}>
          <button className="btn sm danger" onClick={retry}>
            <Icon id="i-refresh" size="sm" />
            Réessayer
          </button>
        </div>
      ) : null}
    </StateFrame>
  );
}

export function ForbiddenState() {
  return (
    <StateFrame icon="i-lock" title="Accès restreint" tone="warn">
      Ton rôle ne donne pas accès à cette section. Si c'est une erreur, vois avec la direction.
    </StateFrame>
  );
}

/**
 * Bandeau de fraîcheur — fondé sur l'âge RÉEL de la copie Vevidental.
 *
 * Il mesurait jusqu'au 2026-08-12 l'âge du cache mémoire du serveur, ce qui le
 * rendait rassurant à tort dans les deux cas qui comptent : un serveur qui
 * redémarre a un cache vide, donc un âge de zéro, et affichait « données
 * fraîches » avec une copie de trois jours ; et une ingestion en panne ne
 * changeait rien tant que quelqu'un ouvrait la page. C'est ce mensonge qui a
 * laissé la copie muette 33 jours sans que personne ne le voie.
 *
 * `copieAgeSeconds` à `null` = aucune passe n'a jamais réussi. Ce n'est pas
 * « frais » : c'est le cas le plus grave, et il se dit autrement.
 */
export function StaleDataBanner({
  cacheAgeSeconds,
  copieAgeSeconds,
}: {
  cacheAgeSeconds: number;
  copieAgeSeconds?: number | null;
}) {
  if (copieAgeSeconds === null) {
    return (
      <div className="banner warn" role="status">
        <Icon id="i-alert" size="sm" />
        <span>
          Aucune synchronisation Vevidental réussie n'est enregistrée — les chiffres de cet écran
          peuvent être incomplets.
        </span>
      </div>
    );
  }
  // Sans l'âge réel (écrans servis par Twenty), on retombe sur l'âge du cache,
  // qui reste un indice — mais on ne prétend rien sur la copie.
  const age = copieAgeSeconds ?? cacheAgeSeconds;
  if (age < 45 * 60) return null; // sous 45 min = plus d'un cycle de cron (15 min) toléré
  const heures = Math.floor(age / 3600);
  const quand = heures >= 1 ? `${String(heures)} h` : `${String(Math.floor(age / 60))} minutes`;
  return (
    <div className="banner warn" role="status">
      <Icon id="i-alert" size="sm" />
      <span>
        Dernière synchronisation Vevidental réussie il y a plus de {quand} — les chiffres de cet
        écran peuvent avoir du retard.
      </span>
    </div>
  );
}

/**
 * La ligne « Synchronisé il y a N min » — sur l'âge RÉEL de la copie.
 *
 * Le bandeau ci-dessus avait été corrigé le 12/08, cette ligne non : six écrans
 * continuaient d'afficher l'âge du cache mémoire. C'est la même erreur, au même
 * endroit de l'œil : un serveur redémarré annonçait « synchronisé il y a
 * 1 min » avec une ingestion morte depuis trois jours.
 *
 * Trois états, jamais confondus : aucune passe réussie · un âge connu · un
 * écran servi par Twenty, qui ne peut rien dire de la copie et le dit.
 */
export function LigneSync({
  cacheAgeSeconds,
  copieAgeSeconds,
}: {
  cacheAgeSeconds: number;
  copieAgeSeconds?: number | null;
}) {
  if (copieAgeSeconds === null) {
    return (
      <span className="sync" title="Aucune passe de synchronisation Vevidental n'a jamais réussi.">
        <span className="pt" />
        Synchronisation Vevidental : jamais
      </span>
    );
  }
  if (copieAgeSeconds === undefined) {
    // Écran servi par Twenty : l'âge du cache est tout ce qu'on sait, et on ne
    // laisse pas croire qu'il parle de la copie Vevidental.
    const min = Math.floor(cacheAgeSeconds / 60);
    return (
      <span className="sync" title="Âge du cache serveur — cet écran ne lit pas la copie Vevidental.">
        <span className="pt" />
        Données en cache depuis {min < 1 ? "moins d'une minute" : `${String(min)} min`}
      </span>
    );
  }
  const min = Math.floor(copieAgeSeconds / 60);
  const heures = Math.floor(copieAgeSeconds / 3600);
  return (
    <span className="sync" title="Temps écoulé depuis la dernière synchronisation Vevidental réussie.">
      <span className="pt" />
      Synchronisé il y a {heures >= 1 ? `${String(heures)} h` : min < 1 ? "moins d'une minute" : `${String(min)} min`}
    </span>
  );
}
